import type React from 'react';
import { type FC, memo, useEffect, useState } from 'react';
import { cn } from '../../utils/ui';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    className?: string;
    src?: string;
    fallbackSrc?: string;
    fit?: 'cover' | 'default';
    isLoading?: boolean; // Optional external loading control
    fetchPriority?: 'auto' | 'high' | 'low';
}

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // Transparent pixel

const Image: FC<ImageProps> = props => {
    const { className = '', src, fallbackSrc, isLoading, onClick, loading, fetchPriority = 'auto', width, height, alt = '', fit = 'cover', ...rest } = props;

    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    const getBaseClassname = () => {
        const baseClassname = 'transition';
        if (fit === 'cover') return cn(baseClassname, 'w-full h-full object-cover object-center');
        return baseClassname;
    };

    // Determine the source to display
    const getSrc = () => {
        if (!src) return TRANSPARENT_PIXEL;
        if (hasError && fallbackSrc) return fallbackSrc;
        if (hasError) return TRANSPARENT_PIXEL;
        return src;
    };

    // Render the image or fallback content
    const renderContent = () => {
        const currentSrc = getSrc();

        if (isLoading) return <LoadingSkeleton className={cn('', className)} />;

        return (
            <img
                {...rest}
                fetchPriority={fetchPriority}
                loading={loading}
                width={width}
                height={height}
                src={currentSrc}
                alt={alt}
                onError={() => {
                    setHasError(true);
                }}
                className={cn(getBaseClassname(), className)}
                onClick={onClick}
            />
        );
    };

    return renderContent();
};

export default memo(Image);
